import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from "react";
import posthog from "posthog-js";
import { supabase } from "./lib/supabase/config";
import {
  loadFromFirestore, saveToFirestore,
  DEFAULT_COMPOUNDS, FRESH_STATE, getInviteCount,
} from "./lib/storage";
import { exportHealthSummary } from "./lib/exportSummary";
import { matchAll, strongestMatchForVial, recallFromRow, isRelevantRecall } from "./lib/recalls";
import { recallEraBadges } from "./lib/badgeChecks";
import { Sun, Moon, Shield, Settings, Calendar } from "lucide-react";
import { PALETTE, ALL_ZONES, MILESTONES_SHOTS, MILESTONES_STREAK, SHIELD_CYCLE_DAYS, SHIELD_MISS_WINDOW_DAYS, TITRATION_PRESETS, ACHIEVEMENT_BADGES, GOALS, DELIVERY_TYPES, TIME_OF_DAY_BUCKETS } from "./lib/constants";
import { buildFullShots, calcRemaining, calcTotalRemaining, budDays, displaySite, byMonth, cmToImperial, convertWeight, isCountedDelivery, isOralDelivery, calcStreak, firstUnshieldedMiss, isCountOnlyPillShot, streakRoseFromLogging } from "./lib/utils";
import { TH } from "./lib/theme";
import { CompoundIcon } from "./components/CompoundIcon";
import { AlertBanner } from "./components/AlertBanner";
import { MilestoneCard } from "./components/MilestoneCard";
import { CheckInModal } from "./components/CheckInModal";
import { WelcomeFlow, HowToGuide } from "./components/HowToGuide";
import { TrackerContext } from "./context/TrackerContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
const DashView = lazy(() => import("./views/DashView").then(m => ({ default: m.DashView })));
const HistoryView = lazy(() => import("./views/HistoryView").then(m => ({ default: m.HistoryView })));
const VaultView = lazy(() => import("./views/VaultView").then(m => ({ default: m.VaultView })));
const SettingsView = lazy(() => import("./views/SettingsView").then(m => ({ default: m.SettingsView })));
const StreakView = lazy(() => import("./views/StreakView").then(m => ({ default: m.StreakView })));
const TrendsView = lazy(() => import("./views/TrendsView").then(m => ({ default: m.TrendsView })));
import { useShotActions } from "./hooks/useShotActions";
import { useVialActions } from "./hooks/useVialActions";
import { useStoredActions } from "./hooks/useStoredActions";
import { useStackImportActions } from "./hooks/useStackImportActions";
import { StackImportSheet } from "./components/StackImportSheet";
import { shotBreakdown, formatBreakdown, vialForShot as resolveShotVial, blendToText, splitShotConc } from "./lib/blend";

const LOGO_WORDMARK = "/myronko-wordmark.svg";
const LOGO_LITE = "/myronko-wordmark-lite.svg";

function checkMilestone(prevStreak, newStreak, prevTotal, newTotal) {
  for (const m of MILESTONES_STREAK) { if (prevStreak < m && newStreak >= m) return { type: 'streak', value: m }; }
  for (const m of MILESTONES_SHOTS) { if (prevTotal < m && newTotal >= m) return { type: 'shots', value: m }; }
  return null;
}

export function Tracker({ user, pendingInvite }) {
  const [stored, setStored] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadRetryTick, setLoadRetryTick] = useState(0);
  const [saveStatus, setSave] = useState("saved");
  const [saveRetryTick, setSaveRetryTick] = useState(0);
  const saveRetryTimer = useRef(null);
  const [darkMode, setDark] = useState(true);
  const [view, setView] = useState("dash");
  const [filter, setFilter] = useState("all");
  const [toast, setToast] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [recalls, setRecalls] = useState([]);
  const [showLogSheet, setShowLogSheet] = useState(false);
  const [logSheetCmpd, setLogSheetCmpd] = useState(null);
  const [editing, setEditing] = useState(null);
  const [vf, setVf] = useState("all");
  const [vaultSort, setVaultSort] = useState("asc");
  const [addVialFor, setAVF] = useState(null);
  const [newVialD, setNVD] = useState({});
  const [editVialId, setEVI] = useState(null);
  const [editVialD, setEVD] = useState({});
  const [stab, setStab] = useState("profile");
  const [cmpdForm, setCF] = useState({});
  const [editCmpd, setEC] = useState(null);
  const [addCmpd, setAC] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showMultiDoseInsights, setShowMultiDoseInsights] = useState(false);
  const [diag, setDiag] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const [showWelcome, setShowWelcome] = useState(() => { try { return !localStorage.getItem('mr_welcome_seen'); } catch { return false; } });
  const [showHowTo, setShowHowTo] = useState(false);
  const [noVialCmpd, setNoVialCmpd] = useState(null);
  const [streakSaves, setStreakSaves] = useState(0);
  const [pillStreakSaves, setPillStreakSaves] = useState(0);
  const [checkIn, setCheckIn] = useState(false);
  const [checkInCmpd, setCheckInCmpd] = useState(null);
  const [postLogCheckIn, setPostLogCheckIn] = useState(null);
  const [streakReminderEnabled, setStreakReminderEnabled] = useState(true);
  const [schedulingEnabled, setSchedulingEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [streakEnabled, setStreakEnabled] = useState(true);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [pillStreak, setPillStreak] = useState(0);
  const [diagL, setDiagL] = useState(false);
  const [userHeight, setUserHeight] = useState("");
  const [heightUnit, setHeightUnit] = useState("cm");
  const [userWeight, setUserWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [bloodGlucoseUnit, setBloodGlucoseUnit] = useState("mg/dL");
  const [doseUnit, setDoseUnit] = useState("mL");
  const [userGender, setUserGender] = useState("");
  const [checkInsEnabled, setCheckInsEnabled] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [clearDataTaps, setClearDataTaps] = useState(0);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [undoShot, setUndoShot] = useState(null);
  const [undoBatch, setUndoBatch] = useState(null);
  const [badgeToast, setBadgeToast] = useState(null);
  const [resumeNudge, setResumeNudge] = useState(null);

  const dm = darkMode;
  const T = TH(dm);

  // Some browsers (notably Firefox, and some Chromium builds) theme native
  // popups like a <select>'s open dropdown off the document root's color-scheme
  // rather than the individual element's — without this, per-input colorScheme
  // (set via fi(dm) in theme.js) darkens the closed field but the dropdown list
  // itself still renders in the browser's default light palette.
  useEffect(() => {
    document.documentElement.style.colorScheme = dm ? 'dark' : 'light';
  }, [dm]);

  const toastTimer = useRef(null);
  const showToast = useCallback(msg => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  }, []);
  const badgeToastTimer = useRef(null);
  const showBadgeToast = useCallback(payload => {
    if (badgeToastTimer.current) clearTimeout(badgeToastTimer.current);
    setBadgeToast(payload);
    badgeToastTimer.current = setTimeout(() => setBadgeToast(null), 4500);
  }, []);
  // Tapping a badge toast opens Dashboard → Stats for Nerds and asks DashView to scroll the badge
  // grid into view (it sits at the bottom of the stats panel, below the fold).
  const [scrollToBadges, setScrollToBadges] = useState(false);
  const openBadges = useCallback(() => {
    setBadgeToast(null); setView('dash'); setShowStats(true); setScrollToBadges(true);
  }, []);

  // Patch state — debounced effect handles the actual save
  const upd = useCallback(patch => {
    setSave("saving");
    setStored(prev => ({ ...prev, ...patch }));
  }, []);

  // Declared early (rather than near its historical call sites further down) because the
  // badge-detection effect below needs it to look up each shot/vial's compound deliveryType.
  const getCmpd = useCallback(k => stored?.compounds?.[k] || DEFAULT_COMPOUNDS[k] || { label: k, shortLabel: k, goal: "recovery", color: "#7890a8", dim: "#182028", text: "#d0dce8", protocols: {}, defaultVol: "0.5", syringeML: 1.0, builtIn: false }, [stored]);

  // Debounced auto-save: fires 600ms after any stored change, retries up to 3× on failure
  useEffect(() => {
    if (!stored || !loaded || !user || user.uid === 'dev-user') return;
    if (saveRetryTimer.current) clearTimeout(saveRetryTimer.current);
    const timer = setTimeout(async () => {
      try {
        await saveToFirestore(user.uid, stored);
        setSave("saved");
      } catch {
        setSave("error");
        showToast("⚠ Save failed — retrying");
        saveRetryTimer.current = setTimeout(() => setSaveRetryTick(t => t + 1), 30000);
      }
    }, 600);
    return () => { clearTimeout(timer); if (saveRetryTimer.current) clearTimeout(saveRetryTimer.current); };
  }, [loaded, showToast, stored, user, saveRetryTick]);

  // Immediate save for the manual sync button
  const forceSave = useCallback(async () => {
    if (!user || user.uid === 'dev-user' || !stored) return;
    if (saveRetryTimer.current) clearTimeout(saveRetryTimer.current);
    setSave("saving");
    try {
      await saveToFirestore(user.uid, stored);
      setSave("saved");
    } catch { setSave("error"); showToast("⚠ Save failed — please retry"); }
  }, [user, stored, showToast]);

  useEffect(() => {
    (async () => {
      if (!user) { setStored({ ...FRESH_STATE }); setLoaded(true); return; }
      if (user.uid === 'dev-user') {
        const { generateDevState } = await import('./lib/devData');
        const dev = generateDevState();
        setStored(dev); setDark(dev.darkMode);
        setUserHeight(dev.userHeight); setHeightUnit(dev.heightUnit);
        setUserWeight(dev.userWeight); setWeightUnit(dev.weightUnit);
        setBloodGlucoseUnit(dev.bloodGlucoseUnit || 'mg/dL');
        setUserGender(dev.userGender); setCheckInsEnabled(dev.checkInsEnabled);
        setSchedulingEnabled(dev.schedulingEnabled !== undefined ? dev.schedulingEnabled : true);
        setNotificationsEnabled(dev.notificationsEnabled || false);
        setStreakEnabled(dev.streakEnabled !== undefined ? dev.streakEnabled : true);
        setStreakReminderEnabled(dev.streakReminderEnabled !== undefined ? dev.streakReminderEnabled : true);
        setStreakSaves(dev.streakSaves ?? 0);
        setLoaded(true); return;
      }
      setLoadFailed(false);
      try {
        const data = await loadFromFirestore(user.uid);
        if (data) {
          const badges = [...(data.badges || [])];
          if (!badges.includes('trendsetter') || !badges.includes('influencer')) {
            const inviteCount = await getInviteCount(user.uid);
            if (inviteCount >= 1 && !badges.includes('trendsetter')) badges.push('trendsetter');
            if (inviteCount >= 10 && !badges.includes('influencer')) badges.push('influencer');
          }
          let finalData = badges.length !== (data.badges || []).length ? { ...data, badges } : data;
          let needsSave = badges.length !== (data.badges || []).length;
          if (!finalData.checkInWeightMigrated) {
            const wu = finalData.weightUnit || 'kg';
            const migratedCheckIns = (finalData.checkIns || []).map(ci =>
              ci.weight != null && !ci.weightUnit ? { ...ci, weightUnit: wu } : ci
            );
            finalData = { ...finalData, checkIns: migratedCheckIns, checkInWeightMigrated: true };
            needsSave = true;
          }
          if (!finalData.heightMigrated) {
            let { userHeight } = finalData;
            if (finalData.heightUnit === 'ft' && userHeight && !userHeight.includes("'")) {
              userHeight = cmToImperial(userHeight);
            }
            finalData = { ...finalData, userHeight, heightMigrated: true };
            needsSave = true;
          }
          setStored(finalData); setDark(finalData.darkMode); setLoaded(true);
          setUserHeight(finalData.userHeight); setHeightUnit(finalData.heightUnit);
          setUserWeight(finalData.userWeight); setWeightUnit(finalData.weightUnit); setDoseUnit(finalData.doseUnit || 'mL');
          setBloodGlucoseUnit(finalData.bloodGlucoseUnit || 'mg/dL');
          setUserGender(finalData.userGender); setCheckInsEnabled(finalData.checkInsEnabled);
          setSchedulingEnabled(finalData.schedulingEnabled !== undefined ? finalData.schedulingEnabled : true);
          setNotificationsEnabled(finalData.notificationsEnabled || false);
          setStreakEnabled(finalData.streakEnabled !== undefined ? finalData.streakEnabled : true);
          setStreakReminderEnabled(finalData.streakReminderEnabled !== undefined ? finalData.streakReminderEnabled : true);
          setStreakSaves(finalData.streakSaves ?? 0);
          setPillStreakSaves(finalData.pillStreakSaves ?? 0);
          if (needsSave) await saveToFirestore(user.uid, finalData);
          return;
        }
      } catch {
        // A real load error (network blip, RLS, transient 5xx) — NOT the same as "no
        // data yet" (loadFromFirestore only returns null for that case now). Falling
        // through to a fresh/empty state here would let the auto-save effect below
        // silently overwrite the user's real cloud data 600ms later, since it can't
        // tell "genuinely new user" apart from "load failed." Leave stored/loaded
        // untouched instead — that keeps the loading screen up (with a retry button)
        // and keeps the auto-save effect's `!stored || !loaded` guard blocking saves
        // until a load actually succeeds.
        setLoadFailed(true);
        showToast('⚠ Failed to load your data — check your connection and retry');
        return;
      }
      setStored({ ...FRESH_STATE }); setLoaded(true);
    })();
  }, [showToast, user, loadRetryTick]);

  // FDA recalls are public, shared rows; matches against this user's vials are derived below and
  // never saved. A failed fetch just means no recall alerts this session.
  useEffect(() => {
    if (!loaded || !user) return;
    let cancelled = false;
    (async () => {
      if (user.uid === 'dev-user') {
        const { DEV_RECALLS } = await import('./lib/devData');
        if (!cancelled) setRecalls(DEV_RECALLS);
        return;
      }
      const { data, error } = await supabase
        .from('recalls').select('*')
        .or('product_type.ilike.%drug%,product_type.ilike.%supplement%')
        .order('published_at', { ascending: false }).limit(1000);
      if (error) { console.warn('[recalls] fetch failed', error.message); return; }
      if (!cancelled) setRecalls((data || []).map(recallFromRow).filter(isRelevantRecall));
    })();
    return () => { cancelled = true; };
  }, [loaded, user]);

  useEffect(() => {
    if (!pendingInvite?.redeemed || !stored) return;
    const badges = stored.badges || [];
    if (!badges.includes('inner_circle')) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      upd({ badges: [...badges, 'inner_circle'] });
      showToast('🔵 Inner Circle badge unlocked!');
    }
  }, [pendingInvite, showToast, stored, upd]);

  useEffect(() => {
    if (!stored) return;
    const currentBadges = stored.badges || [];
    const earned = [];
    const earn = key => { if (!currentBadges.includes(key)) earned.push(key); };

    const injectableShots = (stored.userShots || []).filter(s => !isOralDelivery(getCmpd(s.compound)?.deliveryType));
    const shotCount = injectableShots.length;
    if (shotCount >= 1)   earn('first_stick');
    if (shotCount >= 100) earn('century');
    if (shotCount >= 200) earn('double_century');
    if (shotCount >= 500) earn('five_hundred');

    const pillShotCount = (stored.userShots || []).filter(s => isOralDelivery(getCmpd(s.compound)?.deliveryType)).length;
    if (pillShotCount >= 1)   earn('first_pill');
    if (pillShotCount >= 100) earn('pill_century');
    if (pillShotCount >= 200) earn('pill_double_century');
    if (pillShotCount >= 500) earn('pill_five_hundred');

    const checkIns = stored.checkIns || [];
    if (checkIns.length >= 5)  earn('trend_tracker');
    if (checkIns.length >= 25) earn('data_devotee');

    const withWeight = checkIns.filter(c => c.weight != null).sort((a, b) => new Date(a.date) - new Date(b.date));
    if (withWeight.length >= 2) {
      const wu = weightUnit || 'kg';
      const toWu = c => convertWeight(c.weight, c.weightUnit || wu, wu);
      const peak = Math.max(...withWeight.map(toWu));
      const current = toWu(withWeight[withWeight.length - 1]);
      const loss = peak - current;
      const trough = Math.min(...withWeight.map(toWu));
      const gain = current - trough;
      const td = wu === 'lbs'
        ? { min: 1, s: 5, m: 10, l: 15, xl: 20, xxl: 50, xxxl: 75, c: 100 }
        : { min: 0.5, s: 2.5, m: 5, l: 7, xl: 9, xxl: 22.5, xxxl: 34, c: 45 };
      if (loss >= td.min)   earn('dropping');
      if (loss >= td.s)     earn('five_down');
      if (loss >= td.m)     earn('ten_down');
      if (loss >= td.l)     earn('fifteen_down');
      if (loss >= td.xl)    earn('twenty_down');
      if (loss >= td.xxl)   earn('fifty_down');
      if (loss >= td.xxxl)  earn('seventy_five_down');
      if (loss >= td.c)     earn('hundred_down');
      if (gain >= td.min)   earn('gaining');
      if (gain >= td.s)     earn('five_up');
      if (gain >= td.m)     earn('ten_up');
      if (gain >= td.l)     earn('fifteen_up');
      if (gain >= td.xl)    earn('twenty_up');
      if (gain >= td.xxl)   earn('fifty_up');
    }

    const activeCmpds = Object.keys(stored.compounds || {}).filter(k => !stored.compounds[k]?.archived);
    if (activeCmpds.length >= 2) earn('compound_curious');
    if (activeCmpds.length >= 4) earn('multi_stack');
    if (activeCmpds.some(k => !isOralDelivery(getCmpd(k)?.deliveryType)) && activeCmpds.some(k => isOralDelivery(getCmpd(k)?.deliveryType)))
      earn('hybrid_protocol');

    if (currentStreak >= 7)   earn('week_warrior');
    if (currentStreak >= 30)  earn('month_strong');
    if (currentStreak >= 90)  earn('quarter');
    if (currentStreak >= 365) earn('annual');

    if (pillStreak >= 7)   earn('pill_week');
    if (pillStreak >= 30)  earn('pill_month');
    if (pillStreak >= 90)  earn('pill_quarter');
    if (pillStreak >= 365) earn('pill_year');

    const shots = injectableShots;
    const uniqueZones = new Set(shots.map(s => s.site?.split(':')[0]).filter(Boolean));
    if (uniqueZones.size >= 3)                        earn('site_rotator');
    if (ALL_ZONES.every(z => uniqueZones.has(z.id))) earn('injection_artist');

    if (shots.filter(s => s.note && s.note.trim().length > 2).length >= 25) earn('note_taker');

    const emptyVials = Object.entries(stored.vials || {})
      .filter(([k]) => !isOralDelivery(getCmpd(k)?.deliveryType))
      .flatMap(([, vs]) => vs)
      .filter(v => v.status === 'empty').length;
    if (emptyVials >= 5) earn('vial_veteran');

    if (shots.length > 0) {
      const firstDate = shots.reduce((min, s) => new Date(s.date) < min ? new Date(s.date) : min, new Date(shots[0].date));
      const daysSince = (Date.now() - firstDate.getTime()) / 86400000;
      if (daysSince >= 365) earn('protocol_devotee');
      if (daysSince >= 730) earn('veteran');
    }

    const earlyBird = shots.filter(s => new Date(s.date).getHours() < 8).length;
    const nightOwl  = shots.filter(s => new Date(s.date).getHours() >= 22).length;
    if (earlyBird >= 10) earn('early_bird');
    if (nightOwl  >= 10) earn('night_owl');

    const blendDoses = injectableShots.filter(s => shotBreakdown(s, stored.vials?.[s.compound], getCmpd(s.compound)?.deliveryType).length > 0).length;
    if (blendDoses >= 1)  earn('first_blend');
    if (blendDoses >= 25) earn('blend_master');

    if (Object.values(stored.compounds || {}).some(c => (c.cycleBreaks || []).some(b => b.to != null))) earn('back_in_cycle');
    if (stored.stackImported) earn('quick_start');
    if (stored.clinicianSummaryGenerated) earn('doctors_orders');
    recallEraBadges({ stored, shots: buildFullShots(stored.userShots || []), checkIns: stored.checkIns || [], getCmpd }).forEach(earn);

    if (earned.length > 0) {
      /* eslint-disable react-hooks/set-state-in-effect */
      upd({ badges: [...currentBadges, ...earned] });
      if (earned.length === 1) {
        const def = ACHIEVEMENT_BADGES.find(b => b.id === earned[0]);
        if (def) showBadgeToast({ emoji: def.emoji, label: def.label, count: 1, onTap: openBadges });
      } else {
        showBadgeToast({ count: earned.length, onTap: openBadges });
      }
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  // stored.badges intentionally omitted to avoid grant loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored?.userShots, stored?.checkIns, stored?.compounds, stored?.vials, stored?.stackImported, stored?.clinicianSummaryGenerated, weightUnit, currentStreak, pillStreak]);

  const streakInitialized = useRef(false);
  const injShotCount = useRef(null);
  useEffect(() => {
    if (!stored || !streakEnabled) return;
    const shots = buildFullShots(stored.userShots || []);
    const injectableCompounds = Object.fromEntries(
      Object.entries(stored.compounds || {}).filter(([, c]) => c.deliveryType !== 'pill')
    );
    // Persisted (not just in-memory) record of which specific days a shield already
    // covered -- see calcStreak/firstUnshieldedMiss in lib/utils.js. Durability here is
    // the point: streakSaves/currentStreak are React state, so without a stored list of
    // *which day* was forgiven, a shield's protection evaporated on the very next reload,
    // which is what let a single break-resume miss (see streaks-and-breaks.md) silently
    // undercount the streak even after a shield should have covered it.
    const shieldedDates = stored.streakShieldedDates || [];
    // Mirrors calcStreak's own "is there anything to protect" check -- if every injectable
    // compound was deleted/archived or has no freq, there's nothing being tracked anymore,
    // not a missed dose. A shield exists to cover a real miss, not to paper over "nothing
    // left to have missed."
    const hasScheduled = Object.values(injectableCompounds).some(c => c.freq?.length > 0 && !c.archived);
    /* eslint-disable react-hooks/set-state-in-effect */
    if (hasScheduled && streakSaves > 0) {
      const miss = firstUnshieldedMiss(shots, injectableCompounds, new Date(), shieldedDates);
      if (miss && miss.daysAgo <= SHIELD_MISS_WINDOW_DAYS) {
        const nextSaves = streakSaves - 1;
        setStreakSaves(nextSaves);
        upd({ streakSaves: nextSaves, streakShieldedDates: [...shieldedDates, miss.dayStr] });
        showToast("🛡 Streak shield used — streak protected!");
        return;
      }
    }
    const s = calcStreak(shots, injectableCompounds, new Date(), shieldedDates);
    const injCount = shots.filter(x => injectableCompounds[x.compound]).length;
    const celebrate = streakRoseFromLogging({ initialized: streakInitialized.current, prevStreak: currentStreak, newStreak: s, prevCount: injShotCount.current, newCount: injCount });
    injShotCount.current = injCount;
    if (celebrate) {
      const m = checkMilestone(currentStreak, s, 0, 0);
      if (m) setMilestone(m);
      const prevMilestones = Math.floor(currentStreak / SHIELD_CYCLE_DAYS);
      const newMilestones = Math.floor(s / SHIELD_CYCLE_DAYS);
      if (newMilestones > prevMilestones && streakSaves < 3) {
        const next = Math.min(3, streakSaves + (newMilestones - prevMilestones));
        setStreakSaves(next); upd({ streakSaves: next }); showToast("🛡 Shield earned!");
      }
    }
    streakInitialized.current = true;
    setCurrentStreak(s);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [stored, streakEnabled, currentStreak, streakSaves, upd, showToast]);

  const pillStreakInitialized = useRef(false);
  const pillShotCount = useRef(null);
  useEffect(() => {
    if (!stored || !streakEnabled) return;
    const shots = buildFullShots(stored.userShots || []);
    const pillCompounds = Object.fromEntries(
      Object.entries(stored.compounds || {}).filter(([, c]) => c.deliveryType === 'pill')
    );
    // See streakShieldedDates above -- same durable-protection fix, own persisted field.
    const shieldedDates = stored.pillStreakShieldedDates || [];
    // See hasScheduled above -- same "nothing left to protect" guard.
    const hasScheduled = Object.values(pillCompounds).some(c => c.freq?.length > 0 && !c.archived);
    /* eslint-disable react-hooks/set-state-in-effect */
    if (hasScheduled && pillStreakSaves > 0) {
      const miss = firstUnshieldedMiss(shots, pillCompounds, new Date(), shieldedDates);
      if (miss && miss.daysAgo <= SHIELD_MISS_WINDOW_DAYS) {
        const nextSaves = pillStreakSaves - 1;
        setPillStreakSaves(nextSaves);
        upd({ pillStreakSaves: nextSaves, pillStreakShieldedDates: [...shieldedDates, miss.dayStr] });
        showToast("🛡 Pill streak shield used — streak protected!");
        return;
      }
    }
    const s = calcStreak(shots, pillCompounds, new Date(), shieldedDates);
    const pillCount = shots.filter(x => pillCompounds[x.compound]).length;
    const celebrate = streakRoseFromLogging({ initialized: pillStreakInitialized.current, prevStreak: pillStreak, newStreak: s, prevCount: pillShotCount.current, newCount: pillCount });
    pillShotCount.current = pillCount;
    if (celebrate) {
      const m = checkMilestone(pillStreak, s, 0, 0);
      if (m) setMilestone(m);
      const prevMilestones = Math.floor(pillStreak / SHIELD_CYCLE_DAYS);
      const newMilestones = Math.floor(s / SHIELD_CYCLE_DAYS);
      if (newMilestones > prevMilestones && pillStreakSaves < 3) {
        const next = Math.min(3, pillStreakSaves + (newMilestones - prevMilestones));
        setPillStreakSaves(next); upd({ pillStreakSaves: next }); showToast("🛡 Pill streak shield earned!");
      }
    }
    pillStreakInitialized.current = true;
    setPillStreak(s);
    /* eslint-enable react-hooks/set-state-in-effect */
  // stored.badges intentionally omitted to avoid grant loop (matches the injection streak effect above)
  }, [stored, streakEnabled, pillStreak, pillStreakSaves, upd, showToast]);

  const prevTotalShots = useRef(null);
  useEffect(() => {
    if (!stored) return;
    // Injectable shots only -- matches the badge system's first_stick/century/etc split
    // (see the badge effect above), which already excludes pill doses from these counts.
    // Pill doses have their own separate first_pill/pill_century/etc badges and don't
    // also inflate the injection-shot milestone card.
    const total = (stored.userShots || []).filter(s => !isOralDelivery(getCmpd(s.compound)?.deliveryType)).length;
    if (prevTotalShots.current !== null && total > prevTotalShots.current) {
      const m = checkMilestone(0, 0, prevTotalShots.current, total);
      if (m) setMilestone(m);
    }
    prevTotalShots.current = total;
  }, [stored, getCmpd]);

  const recallMatches = useMemo(() => (stored ? matchAll(stored, recalls) : []), [stored, recalls]);

  useEffect(() => {
    if (!stored) return;
    const allShots = buildFullShots(stored.userShots || []);
    const newAlerts = [];
    Object.keys(stored.compounds || {}).forEach(k => {
      const av = (stored.vials[k] || []).find(v => v.active && v.status === "open");
      if (av) {
        const rem = calcRemaining(av, allShots);
        const c = getCmpd(k);
        const isPen = isCountedDelivery(c?.deliveryType);
        const vaultVials = (stored.vials[k] || []).filter(v => v.status !== "archived" && v.status !== "empty");
        const totalRemVol = calcTotalRemaining(vaultVials, allShots, isPen, k);
        const totalCap = vaultVials.reduce((sum, v) => sum + (isPen ? (parseInt(v.totalDoses) || 0) : (parseFloat(v.totalVol) || 0)), 0);
        const totalPct = totalCap > 0 ? (totalRemVol / totalCap * 100) : 0;
        const rid = `reorder-${av.id}`;
        if (totalPct <= 35 && totalPct > 0 && !dismissed.has(rid)) {
          const remStr = isPen ? `${Math.round(totalRemVol)} doses` : `${totalRemVol.toFixed(1)}mL`;
          newAlerts.push({ id: rid, type: "supply_low", message: `${c.label}: ${remStr} total remaining (${Math.round(totalPct)}%) — consider reordering` });
        }
        if (av.bud) {
          const bd = budDays(av.bud);
          const bid = `bud-${av.id}`;
          const pct2 = av.totalVol > 0 ? (rem / av.totalVol * 100) : 100;
          // Already-past-BUD gets its own persistent per-vial warning (BudWarning, on the
          // compound card / Vault) instead of duplicating here — this banner is only for the
          // proactive "still good, but won't be for long" nudge. Its dismiss is likewise a
          // persisted per-vial flag (av.budApproachDismissed, not the old in-memory `dismissed`
          // Set), so it doesn't resurface on every reload the way `dismissed` always has.
          if (bd !== null && bd >= 0 && bd <= 30 && pct2 <= 35 && !av.budApproachDismissed)
            newAlerts.push({ id: bid, type: "bud", vialId: av.id, message: `${getCmpd(k).label}: BUD approaching (${av.bud}, ${bd}d away) — review with your provider` });
        }
      }
    });
    // Recall alerts go first. Finished vials, vials the user marked themselves, and recalls the
    // user already acknowledged on this vial never raise a banner (the vial's tag still shows).
    const recallAlerts = [];
    recallMatches.forEach(m => {
      if (m.finished) return;
      const vial = (stored.vials[m.compoundKey] || []).find(v => v.id === m.vialId);
      if (!vial || vial.recall || vial.recallAcks?.[m.recall.id]) return;
      const c = getCmpd(m.compoundKey);
      const noun = isOralDelivery(c?.deliveryType) ? 'bottle' : isCountedDelivery(c?.deliveryType) ? 'pen' : 'vial';
      recallAlerts.push({ id: `recall-${m.vialId}-${m.recall.id}`, type: 'recall', compoundKey: m.compoundKey, vialId: m.vialId, recallId: m.recall.id, match: m.match, lot: vial.lot || '', compoundLabel: c?.label || 'stack item', noun, recall: m.recall });
    });
    newAlerts.unshift(...recallAlerts);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAlerts(newAlerts);
  }, [stored, dismissed, getCmpd, recallMatches]);

  useEffect(() => {
    try { const v = parseInt(localStorage.getItem('mr_v') || '0') + 1; localStorage.setItem('mr_v', String(v)); } catch {}
  }, []);

  useEffect(() => {
    const onPrompt = e => { e.preventDefault(); setInstallPrompt(e); setShowInstallPrompt(true); };
    const onInstalled = () => { setShowInstallPrompt(false); setInstallPrompt(null); showToast("✨ MyRONKO installed!"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, [showToast]);

  const openLogSheet = useCallback(cmpd => { setLogSheetCmpd(cmpd); setShowLogSheet(true); }, []);

  // Action hooks
  const { handleLogShotFromSheet, handleOneTapLog, handleLogBatch, deleteShot, doUndoShot, doUndoBatch, startEdit, saveEdit } = useShotActions({
    stored, upd, user, getCmpd, showToast, logSheetCmpd, setShowLogSheet, setLogSheetCmpd, editing, setEditing, undoShot, setUndoShot, undoBatch, setUndoBatch,
  });
  const { saveNewVial, saveVialEdit, duplicateVial, archiveVial, unarchiveVial, deleteVial, setActiveV, ackBudExpired, dismissBudWarning, dismissBudApproaching, ackRecall, stopUsingRecalledVial, markVialRecalled, unmarkVialRecalled } = useVialActions({
    stored, upd, getCmpd, showToast, setPendingConfirm, newVialD, setAVF, setNVD, editVialId, editVialD, setEVI, setEVD,
  });
  // supply_low alerts still use the plain in-memory `dismissed` Set (unchanged, resets each
  // session); a "bud" alert instead routes to the persisted per-vial budApproachDismissed flag
  // so it doesn't resurface on reload the way the old shared Set always did.
  const dismissAlert = useCallback(a => {
    if (a.type === 'recall') { ackRecall(a.compoundKey, a.vialId, a.recallId); return; }
    if (a.type === "bud" && a.vialId) { dismissBudApproaching(a.vialId); return; }
    setDismissed(prev => new Set([...prev, a.id]));
  }, [dismissBudApproaching, ackRecall]);
  const { saveCmpd, deleteCmpd, archiveCmpd, startEditCmpd, toggleZone, toggleHiddenCmpd, stepUpTitration, holdTitration, addCustomZone, removeCustomZone, updateCheckIn, deleteCheckIn, saveCheckIn, startBreak, endBreak } = useStoredActions({
    stored, upd, user, getCmpd, showToast, cmpdForm, setAC, setEC, setCF, setPendingConfirm, setCheckIn, setPostLogCheckIn, setUserWeight, weightUnit, bloodGlucoseUnit, setResumeNudge,
  });
  const {
    showImportSheet, setShowImportSheet, importFiles, setImportFiles, importPlan, setImportPlan,
    importParseError, setImportParseError, parseImportFiles, commitImport,
  } = useStackImportActions({ stored, upd, showToast });

  const allShots = useMemo(() => buildFullShots(stored?.userShots || []), [stored?.userShots]);
  const cmpdKeys = useMemo(() => Object.keys(stored?.compounds || DEFAULT_COMPOUNDS).filter(k => !stored?.compounds?.[k]?.archived), [stored?.compounds]);

  const exportSummary = useCallback((rangeDays = 90) => {
    const result = exportHealthSummary({ allShots, checkIns: stored.checkIns || [], stored, getCmpd, cmpdKeys, currentStreak, pillStreak, streakEnabled, weightUnit, bloodGlucoseUnit, rangeDays, recalls });
    if (result === 'downloaded') showToast('Popup blocked — summary downloaded instead');
    if (!stored.clinicianSummaryGenerated) upd({ clinicianSummaryGenerated: true });
  }, [allShots, stored, getCmpd, cmpdKeys, currentStreak, pillStreak, streakEnabled, weightUnit, bloodGlucoseUnit, showToast, upd, recalls]);

  const exportJSON = useCallback(() => {
    const s = buildFullShots(stored.userShots || []);
    const p = { ...stored, version: "myronko-1.0", exportedAt: new Date().toISOString(), allShotsCount: s.length };
    const uri = "data:application/json;charset=utf-8," + encodeURIComponent(JSON.stringify(p, null, 2));
    const a = document.createElement("a"); a.href = uri; a.download = `myronko-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); showToast("JSON exported ✓");
  }, [stored, showToast]);

  const exportCSV = useCallback(() => {
    const s = buildFullShots(stored.userShots || []);
    // Quote/escape on comma, embedded double-quote, OR newline (CR/LF) -- not just comma.
    // A value with no comma but an embedded quote or newline still corrupts the CSV's row
    // structure if left unquoted (shot notes come from a <textarea> and can span lines).
    const esc = v => (/["\n\r,]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
    const rows = [["Shot #", "Date", "Time", "Stack Item", "Delivery", "Site", "Volume (mL)", "Dose (mg)", "Protocol", "Note", "Vial", "Titration Step", "Dose Index", "Ingredients"]];
    s.forEach(x => {
      const c = getCmpd(x.compound);
      const isPen = isCountedDelivery(c.deliveryType);
      const proto = c.protocols?.[x.protocol];
      const volML = isPen ? "" : x.vol;
      const vialForShot = (stored.vials?.[x.compound] || []).find(v => v.id === x.vial);
      // mg uses History's vial fallback (active/open vial when none was recorded) so it agrees with the
      // Ingredients column; the Vial column below still names only an exactly-recorded vial.
      const doseVial = resolveShotVial(stored.vials?.[x.compound], x.vial);
      const doseConc = splitShotConc(x, stored.vials?.[x.compound], c.deliveryType) ?? (proto?.conc || (doseVial?.conc ? parseFloat(doseVial.conc) : null));
      const doseMg = isPen ? (isCountOnlyPillShot(x) ? "" : x.vol) : doseConc ? parseFloat((parseFloat(x.vol || 0) * doseConc).toFixed(1)) : "";
      const delivery = isPen ? (c.deliveryType === 'autoinjector' ? 'Auto-injector' : c.deliveryType === 'pill' ? 'Pill' : 'Pen') : (c.deliveryType === 'compounded' ? 'Compounded' : 'Syringe');
      const d = new Date(x.date);
      const protocolCol = (() => {
        const t = c.titration;
        if (t && doseMg !== '') {
          if (!t.preset) return t.customDose ? `Custom · ${doseMg}mg` : (proto?.label || x.protocol || '');
          const preset = TITRATION_PRESETS[t.preset];
          if (preset) return `${preset.label} · ${doseMg}mg`;
        }
        return proto?.label || x.protocol || '';
      })();
      const vialCol = vialForShot ? [vialForShot.label, vialForShot.pharmacy, vialForShot.lot ? `Lot ${vialForShot.lot}` : ''].filter(Boolean).join(' · ') : '';
      const titrationStepCol = (() => {
        const t = c.titration;
        if (!t) return '';
        if (!t.preset) return t.customDose ? `Custom ${t.customDose}mg` : '';
        const preset = TITRATION_PRESETS[t.preset];
        if (!preset || !t.startDate) return '';
        const weeksSinceStart = (new Date(x.date) - new Date(t.startDate)) / (7 * 86400000);
        // Weeks spent on hold (paused) at a given step push out when the NEXT step actually
        // started -- without this, a hold makes the pure-calendar estimate below overstate
        // how far along a shot's date really was. archivedHolds records this once a hold's
        // step is stepped past (see stepUpTitration in useStoredActions.js).
        const holdWeeksForStep = stepI => (t.archivedHolds || []).filter(h => h.step === stepI).reduce((sum, h) => sum + (h.weeks || 0), 0);
        let stepIdx = 0, cumWeeks = 0;
        for (let i = 0; i < preset.steps.length; i++) {
          if (i === preset.steps.length - 1 || !preset.steps[i].weeks) { stepIdx = i; break; }
          cumWeeks += preset.steps[i].weeks + holdWeeksForStep(i);
          if (weeksSinceStart < cumWeeks) { stepIdx = i; break; }
          stepIdx = i + 1;
        }
        // Never report a step beyond what the compound has actually recorded reaching --
        // calendar math alone can't see a still-in-progress hold on the CURRENT step.
        if (t.currentStep != null) stepIdx = Math.min(stepIdx, t.currentStep);
        const step = preset.steps[stepIdx];
        return step ? `${step.label} (${step.dose}mg)` : '';
      })();
      const ingredientsCol = formatBreakdown(shotBreakdown(x, stored.vials?.[x.compound], c.deliveryType));
      const doseIndexCol = x.totalDoses > 1 ? `${(x.injectionIndex ?? 0) + 1} of ${x.totalDoses}` : '';
      const siteCol = isOralDelivery(c.deliveryType) ? '' : displaySite(x.site);
      rows.push([x.shotNumber, d.toLocaleDateString("en-US"), d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }), esc(c.label), delivery, siteCol, volML, doseMg, protocolCol, esc(x.note || ""), esc(vialCol), titrationStepCol, doseIndexCol, esc(ingredientsCol)]);
    });
    const uri = "data:text/csv;charset=utf-8," + encodeURIComponent(rows.map(r => r.join(",")).join("\n"));
    const a = document.createElement("a"); a.href = uri; a.download = `myronko-shots-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); showToast("CSV exported ✓");
  }, [stored, getCmpd, showToast]);

  const exportCheckInsCSV = useCallback(() => {
    // Quote/escape on comma, embedded double-quote, OR newline (CR/LF) -- not just comma.
    // A value with no comma but an embedded quote or newline still corrupts the CSV's row
    // structure if left unquoted (shot notes come from a <textarea> and can span lines).
    const esc = v => (/["\n\r,]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
    const rows = [["Date", "Time", "Mood", "Symptoms", "Weight", "Weight Unit", "Blood Glucose", "Blood Glucose Unit", "Systolic", "Diastolic", "Heart Rate (BPM)", "Linked Stack Item", "Notes"]];
    (stored.checkIns || []).forEach(ci => {
      const d = new Date(ci.date);
      const symptoms = (ci.symptoms || []).join(';');
      const linkedLabel = ci.linkedCmpd ? (getCmpd(ci.linkedCmpd)?.label || ci.linkedCmpd) : '';
      rows.push([
        d.toLocaleDateString("en-US"), d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        ci.mood ?? '', esc(symptoms), ci.weight ?? '', ci.weight != null ? (ci.weightUnit || weightUnit) : '',
        ci.bloodGlucose ?? '', ci.bloodGlucose != null ? (ci.bloodGlucoseUnit || bloodGlucoseUnit) : '',
        ci.bloodPressureSystolic ?? '', ci.bloodPressureDiastolic ?? '', ci.heartRate ?? '',
        esc(linkedLabel), esc((ci.notes || "").replace(/,/g, ";")),
      ]);
    });
    const uri = "data:text/csv;charset=utf-8," + encodeURIComponent(rows.map(r => r.join(",")).join("\n"));
    const a = document.createElement("a"); a.href = uri; a.download = `myronko-checkins-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); showToast("Check-ins CSV exported ✓");
  }, [stored, getCmpd, showToast, weightUnit, bloodGlucoseUnit]);

  const exportVialsCSV = useCallback(() => {
    // Quote/escape on comma, embedded double-quote, OR newline (CR/LF) -- not just comma.
    // A value with no comma but an embedded quote or newline still corrupts the CSV's row
    // structure if left unquoted (shot notes come from a <textarea> and can span lines).
    const esc = v => (/["\n\r,]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
    const rows = [["Stack Item", "Label", "Status", "Active", "Total Volume (mL)", "Total Doses", "Concentration (mg/mL)", "Pharmacy", "Lot #", "BUD", "Opened Date", "Cost", "Pills per Dose", "Blend Ingredients", "Recalled"]];
    const recalledCol = v => {
      const m = strongestMatchForVial(recallMatches, v.id);
      return [m ? (m.match === 'lot' ? 'FDA (lot match)' : 'FDA (possible)') : '', v.recall ? 'Manual' : ''].filter(Boolean).join('; ');
    };
    Object.entries(stored.vials || {}).forEach(([cmpdKey, vials]) => {
      const c = getCmpd(cmpdKey);
      const isOral = isOralDelivery(c?.deliveryType);
      (vials || []).forEach(v => {
        const isCounted = v.totalDoses != null;
        rows.push([
          esc(c.label), esc(v.label || ''), v.status || '', v.active ? 'Yes' : 'No',
          isCounted ? '' : (v.totalVol ?? ''), isCounted ? v.totalDoses : '', isCounted ? '' : (v.conc ?? ''),
          esc(v.pharmacy || ''), esc(v.lot || ''), v.bud || '', v.openedAt || '', v.costPerVial ?? '',
          isOral ? (v.pillsPerDose ?? '') : '',
          // Same "Name mg; Name mg" text Stack Import's Blend Ingredients column reads, so it round-trips.
          esc(blendToText(v.blend)),
          esc(recalledCol(v)),
        ]);
      });
    });
    const uri = "data:text/csv;charset=utf-8," + encodeURIComponent(rows.map(r => r.join(",")).join("\n"));
    const a = document.createElement("a"); a.href = uri; a.download = `myronko-vials-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); showToast("Vials CSV exported ✓");
  }, [stored, getCmpd, showToast, recallMatches]);

  const exportCompoundsCSV = useCallback(() => {
    // Quote/escape on comma, embedded double-quote, OR newline (CR/LF) -- not just comma.
    // A value with no comma but an embedded quote or newline still corrupts the CSV's row
    // structure if left unquoted (shot notes come from a <textarea> and can span lines).
    const esc = v => (/["\n\r,]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
    const deliveryLabel = Object.fromEntries(DELIVERY_TYPES.map(d => [d.id, d.label]));
    const timeOfDayLabel = c => TIME_OF_DAY_BUCKETS.find(b => b.value === (c.timeOfDay || null))?.label || 'Anytime';
    const rows = [["Full Name", "Short Name", "Delivery Type", "Goal", "Dose Days", "Time of Day", "Archived", "Blend Ingredients", "Default BAC (mL)"]];
    Object.values(stored.compounds || {}).forEach(c => {
      const doseDays = (c.freq || []).join(',');
      rows.push([
        esc(c.label || ''), esc(c.shortLabel || ''), deliveryLabel[c.deliveryType] || c.deliveryType || '',
        GOALS[c.goal]?.label || c.goal || '', esc(doseDays), timeOfDayLabel(c), c.archived ? 'Yes' : 'No',
        esc(blendToText(c.blend)), c.blend ? (c.bacMl ?? '') : '',
      ]);
    });
    const uri = "data:text/csv;charset=utf-8," + encodeURIComponent(rows.map(r => r.join(",")).join("\n"));
    const a = document.createElement("a"); a.href = uri; a.download = `myronko-compounds-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); showToast("Compounds CSV exported ✓");
  }, [stored, showToast]);

  const exportPillsCSV = useCallback(() => {
    // Quote/escape on comma, embedded double-quote, OR newline (CR/LF) -- not just comma.
    // A value with no comma but an embedded quote or newline still corrupts the CSV's row
    // structure if left unquoted (shot notes come from a <textarea> and can span lines).
    const esc = v => (/["\n\r,]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
    const rows = [["Shot #", "Date", "Time", "Stack Item", "Pill Count", "Mg per Pill", "Total Mg", "Note"]];
    const s = buildFullShots(stored.userShots || []).filter(x => isOralDelivery(getCmpd(x.compound)?.deliveryType));
    s.forEach(x => {
      const c = getCmpd(x.compound);
      const d = new Date(x.date);
      const countOnly = isCountOnlyPillShot(x);
      rows.push([
        x.shotNumber, d.toLocaleDateString("en-US"), d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        esc(c.label), x.pillCount ?? '', countOnly ? '' : (x.mgPerPill ?? ''), countOnly ? '' : x.vol,
        esc(x.note || ""),
      ]);
    });
    const uri = "data:text/csv;charset=utf-8," + encodeURIComponent(rows.map(r => r.join(",")).join("\n"));
    const a = document.createElement("a"); a.href = uri; a.download = `myronko-pills-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); showToast("Pills CSV exported ✓");
  }, [stored, getCmpd, showToast]);

  const runDiag = useCallback(async () => {
    setDiagL(true);
    const results = {};
    for (const key of ["stack_tracker_v1", "nad_tracker_v3", "nad_tracker_v2", "nad_tracker_v1"]) {
      try {
        const res = await window.storage?.get(key);
        if (res?.value) { const bytes = new TextEncoder().encode(res.value).length; let shots = 0; try { const p = JSON.parse(res.value); shots = p.shots?.length || p.userShots?.length || 0; } catch {} results[key] = { found: true, bytes, kb: (bytes / 1024).toFixed(1), shots }; }
        else { results[key] = { found: false }; }
      } catch (e) { results[key] = { found: false, error: e.message }; }
    }
    let writeTest = "unknown";
    try { await window.storage?.set("_diag_test", JSON.stringify({ ts: Date.now() })); await window.storage?.delete("_diag_test"); writeTest = "pass"; } catch (e) { writeTest = "fail: " + e.message; }
    let payloadSize = 0;
    try { payloadSize = new TextEncoder().encode(JSON.stringify({ userShots: stored.userShots || [], counters: stored.counters, vials: stored.vials })).length; } catch {}
    setDiag({ keys: results, writeTest, payloadSize: (payloadSize / 1024).toFixed(1) }); setDiagL(false);
  }, [stored]);

  useEffect(() => {
    const keys = Object.keys(stored?.compounds || DEFAULT_COMPOUNDS).filter(k => !stored?.compounds?.[k]?.archived);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (keys.length === 0 && stab !== "compounds" && stab !== "appdata" && stab !== "profile") setStab("compounds");
    // Zones tab is injectable-only — bounce off it if the last non-pill compound was
    // removed/archived while it was open (its tab button would otherwise vanish under it).
    else if (stab === "zones" && !keys.some(k => stored?.compounds?.[k]?.deliveryType !== 'pill'))
      setStab("compounds");
  }, [stored?.compounds, stab]);

  const filtered = useMemo(() => {
    if (filter === "all") return allShots;
    if (filter === "pills") return allShots.filter(s => isOralDelivery(getCmpd(s.compound)?.deliveryType));
    return allShots.filter(s => s.compound === filter);
  }, [allShots, filter, getCmpd]);
  const groups = useMemo(() => byMonth(filtered), [filtered]);
  const allVials = useMemo(() => cmpdKeys.flatMap(k => (stored?.vials?.[k] || []).filter(v => v.status !== "archived").map(v => ({ ...v, compound: k, remainingCalc: calcRemaining(v, allShots) }))), [cmpdKeys, stored?.vials, allShots]);
  const archivedVials = useMemo(() => cmpdKeys.flatMap(k => (stored?.vials?.[k] || []).filter(v => v.status === "archived").map(v => ({ ...v, compound: k }))), [cmpdKeys, stored?.vials]);
  const vaultV = useMemo(() => {
    const base = vf === "all" ? allVials
      : vf === "pills" ? allVials.filter(v => isOralDelivery(getCmpd(v.compound)?.deliveryType))
      : allVials.filter(v => v.compound === vf);
    return [...base].sort((a, b) => {
      const aActive = a.active && a.status !== "empty" ? 0 : 1;
      const bActive = b.active && b.status !== "empty" ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      if (!a.bud && !b.bud) return 0;
      if (!a.bud) return 1; if (!b.bud) return -1;
      const diff = new Date(a.bud) - new Date(b.bud);
      return vaultSort === "asc" ? diff : -diff;
    });
  }, [allVials, vf, vaultSort, getCmpd]);
  const visZones = useMemo(() => [...ALL_ZONES.filter(z => !(stored?.hiddenZones || []).includes(z.id)), ...(stored?.customZones || [])], [stored?.hiddenZones, stored?.customZones]);

  const onAddVial = useCallback(cmpd => { setShowLogSheet(false); setLogSheetCmpd(null); setView("vault"); setAVF(true); setNVD({ status: "new", active: true, quantity: 1, compound: cmpd }); }, [setShowLogSheet, setLogSheetCmpd, setView, setAVF, setNVD]);
  const onSetupFirstCompound = useCallback(() => { setView("settings"); setStab("compounds"); setAC(true); setCF({ goal: "recovery", color: PALETTE[2].color, syringeML: 1.0, freq: [] }); }, [setView, setStab, setAC, setCF]);
  const onGoToVault = useCallback(() => { setShowLogSheet(false); setLogSheetCmpd(null); setView("vault"); }, [setShowLogSheet, setLogSheetCmpd, setView]);

  if (!loaded || !stored) return (
    <div style={{ background: dm ? "#080c10" : "#f0f4f8", height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, fontFamily: "Georgia,serif" }}>
      <img src={dm ? LOGO_WORDMARK : LOGO_LITE} alt="MyRONKO" style={{ width: 200, height: "auto" }} />
      {loadFailed ? (
        <>
          <div style={{ color: "#e84040", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.05em", textAlign: "center", maxWidth: 280 }}>Couldn't load your data — check your connection.</div>
          <button onClick={() => setLoadRetryTick(t => t + 1)} style={{ padding: "9px 22px", borderRadius: 20, cursor: "pointer", fontSize: 11, fontFamily: "monospace", fontWeight: "700", background: "rgba(0,216,240,0.1)", color: dm ? "#c0e8f0" : "#003850", border: "1px solid rgba(0,216,240,0.35)" }}>Retry</button>
        </>
      ) : (
        <div style={{ color: dm ? "rgba(0,216,240,0.3)" : "rgba(0,80,130,0.4)", fontFamily: "monospace", fontSize: 10, letterSpacing: "0.14em" }}>loading...</div>
      )}
    </div>
  );

  const contextValue = {
    user, stored, upd, getCmpd, dm, showToast,
    allShots, cmpdKeys, allVials, archivedVials, vaultV, filtered, groups, visZones,
    view, setView, filter, setFilter,
    currentStreak, streakEnabled, setStreakEnabled, streakSaves,
    pillStreak, pillStreakSaves,
    showHowTo, setShowHowTo,
    streakReminderEnabled, setStreakReminderEnabled,
    checkInsEnabled, setCheckInsEnabled, setCheckIn, setCheckInCmpd,
    showLogSheet, setShowLogSheet, logSheetCmpd, setLogSheetCmpd, openLogSheet, handleLogShotFromSheet, handleOneTapLog, handleLogBatch,
    editing, setEditing, startEdit, saveEdit, deleteShot,
    checkIns: stored.checkIns || [], deleteCheckIn, updateCheckIn, weightUnit, setWeightUnit, doseUnit, setDoseUnit,
    bloodGlucoseUnit, setBloodGlucoseUnit,
    vf, setVf, addVialFor, newVialD, setNVD, saveNewVial, setAVF,
    editVialId, editVialD, setEVD, setEVI, saveVialEdit,
    duplicateVial, archiveVial, deleteVial, unarchiveVial, setActiveV, ackBudExpired, dismissBudWarning,
    recalls, recallMatches, ackRecall, stopUsingRecalledVial, markVialRecalled, unmarkVialRecalled,
    dismissed, setDismissed, vaultSort, setVaultSort,
    stab, setStab, addCmpd, setAC, editCmpd, setEC, cmpdForm, setCF,
    saveCmpd, startEditCmpd, archiveCmpd, deleteCmpd, startBreak, endBreak,
    showStats, setShowStats, showMultiDoseInsights, setShowMultiDoseInsights,
    scrollToBadges, setScrollToBadges,
    userHeight, setUserHeight, heightUnit, setHeightUnit,
    userWeight, setUserWeight, userGender, setUserGender,
    notificationsEnabled, setNotificationsEnabled, schedulingEnabled, setSchedulingEnabled,
    dashHiddenCmpds: stored.dashHiddenCmpds || [], toggleHiddenCmpd,
    stepUpTitration, holdTitration, toggleZone, addCustomZone, removeCustomZone,
    exportJSON, exportCSV, exportSummary, exportCheckInsCSV, exportVialsCSV, exportCompoundsCSV, exportPillsCSV,
    showImportSheet, setShowImportSheet, importFiles, setImportFiles, importPlan, setImportPlan,
    importParseError, parseImportFiles, commitImport,
    runDiag, diag, diagL, clearDataTaps, setClearDataTaps, setPendingConfirm,
    onAddVial, onSetupFirstCompound, onGoToVault,
  };

  // Nav streak chip: show whichever streak the user actually has. Injectable-only and
  // mixed accounts see the injection streak (matches StreakView's left-column priority);
  // pill-only accounts see the pill streak instead of a permanent, un-fillable "0".
  const hasInjectableCmpd = cmpdKeys.some(k => getCmpd(k)?.deliveryType !== 'pill');
  const navStreak = hasInjectableCmpd ? currentStreak : pillStreak;
  const navStreakSaves = hasInjectableCmpd ? streakSaves : pillStreakSaves;

  const PB = (label, active, onClick) => (
    <button onClick={onClick} style={{ border: `1px solid ${active ? (dm ? "rgba(0,216,240,0.45)" : "rgba(0,112,168,0.45)") : T.BORDER}`, background: active ? (dm ? "rgba(0,216,240,0.1)" : "rgba(0,112,168,0.1)") : "transparent", color: active ? T.CYAN : T.DIM, cursor: "pointer", borderRadius: 20, padding: "5px 13px", fontSize: 11, fontFamily: "monospace", letterSpacing: "0.07em", transition: "all 0.18s" }}>{label}</button>
  );

  return (
    <TrackerContext.Provider value={contextValue}>
    <div style={{ minHeight: "100vh", background: T.BG, fontFamily: "Georgia,'Times New Roman',serif", color: T.TEXT, transition: "background 0.3s,color 0.3s" }}>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", background: dm ? "radial-gradient(ellipse at 10% 0%,rgba(0,216,240,0.06) 0%,transparent 50%),radial-gradient(ellipse at 90% 100%,rgba(242,107,59,0.04) 0%,transparent 50%)" : "" }} />
      {toast && <div style={{ position: "fixed", top: 18, left: "50%", transform: "translateX(-50%)", background: dm ? "#0a1828" : "#e8f4ff", color: dm ? "#00d8f0" : "#006090", padding: "9px 22px", borderRadius: 40, fontSize: 12, fontFamily: "monospace", zIndex: 999, border: `1px solid ${dm ? "rgba(0,216,240,0.35)" : "rgba(0,112,168,0.35)"}`, boxShadow: "0 4px 24px rgba(0,0,0,0.2)", whiteSpace: "nowrap" }}>{toast}</div>}
      {badgeToast && (
        <div data-testid="badge-toast" onClick={badgeToast.onTap} style={{ position: "fixed", top: 18, left: "50%", transform: "translateX(-50%)", background: dm ? "#1c0e06" : "#fff3ed", color: dm ? "#f8a87c" : "#b84a1a", padding: "14px 28px", borderRadius: 18, fontFamily: "monospace", zIndex: 1000, border: `1px solid ${dm ? "rgba(242,107,59,0.4)" : "rgba(242,107,59,0.35)"}`, boxShadow: dm ? "0 8px 36px rgba(242,107,59,0.3)" : "0 6px 28px rgba(242,107,59,0.15)", cursor: badgeToast.onTap ? "pointer" : "default", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, animation: "su 0.3s ease", textAlign: "center", minWidth: 160 }}>
          {badgeToast.count === 1 ? (<>
            <div style={{ fontSize: 30, lineHeight: 1 }}>{badgeToast.emoji}</div>
            <div style={{ fontSize: 9, letterSpacing: "0.14em", opacity: 0.55, marginTop: 2 }}>BADGE UNLOCKED</div>
            <div style={{ fontWeight: 700, letterSpacing: "0.06em", fontSize: 13 }}>{badgeToast.label}</div>
            <div style={{ fontSize: 9, letterSpacing: "0.12em", opacity: 0.55, marginTop: 1 }}>TAP TO VIEW IN STATS →</div>
          </>) : (<>
            <div style={{ fontSize: 26, lineHeight: 1 }}>🏆</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{badgeToast.count} badges unlocked</div>
            <div style={{ fontSize: 9, letterSpacing: "0.12em", opacity: 0.55, marginTop: 1 }}>TAP TO VIEW IN STATS →</div>
          </>)}
        </div>
      )}
      {pendingConfirm && <div style={{ position: "fixed", bottom: 88, left: "50%", transform: "translateX(-50%)", background: dm ? "#0d1820" : "#fff", border: "1px solid rgba(232,80,80,0.45)", borderRadius: 14, padding: "14px 18px", zIndex: 998, boxShadow: "0 4px 24px rgba(0,0,0,0.4)", fontFamily: "monospace", display: "flex", flexDirection: "column", gap: 12, minWidth: 240, animation: "su 0.2s ease" }}>
        <div style={{ fontSize: 12, color: dm ? "#c0d0d8" : "#1a2332", lineHeight: 1.5 }}>{pendingConfirm.message}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { pendingConfirm.onConfirm(); setPendingConfirm(null); }} style={{ flex: 1, padding: "8px", background: "rgba(232,80,80,0.1)", border: "1px solid rgba(232,80,80,0.4)", borderRadius: 8, color: "#e84040", cursor: "pointer", fontSize: 11, fontWeight: "600", fontFamily: "monospace" }}>{pendingConfirm.confirmLabel || 'Delete'}</button>
          <button onClick={() => setPendingConfirm(null)} style={{ flex: 1, padding: "8px", background: "transparent", border: `1px solid ${T.BORDER}`, borderRadius: 8, color: T.DIM, cursor: "pointer", fontSize: 11, fontFamily: "monospace" }}>Cancel</button>
        </div>
      </div>}
      {resumeNudge && <div data-testid="resume-nudge" style={{ position: "fixed", bottom: 88, left: "50%", transform: "translateX(-50%)", background: dm ? "#0d1820" : "#fff", border: `1px solid ${T.BORDER}`, borderRadius: 14, padding: "14px 18px", zIndex: 998, boxShadow: "0 4px 24px rgba(0,0,0,0.4)", fontFamily: "monospace", display: "flex", flexDirection: "column", gap: 12, minWidth: 240, animation: "su 0.2s ease" }}>
        <div style={{ fontSize: 12, color: dm ? "#c0d0d8" : "#1a2332", lineHeight: 1.5 }}>Resumed {resumeNudge.label} — check your schedule & vial supply?</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button data-testid="resume-nudge-review" onClick={() => { setView("settings"); setStab("compounds"); startEditCmpd(resumeNudge.key); setResumeNudge(null); }} style={{ flex: 1, padding: "8px", background: "rgba(0,216,240,0.1)", border: "1px solid rgba(0,216,240,0.3)", borderRadius: 8, color: dm ? "#c0e8f0" : "#003850", cursor: "pointer", fontSize: 11, fontWeight: "600", fontFamily: "monospace" }}>Review</button>
          <button data-testid="resume-nudge-dismiss" onClick={() => setResumeNudge(null)} style={{ flex: 1, padding: "8px", background: "transparent", border: `1px solid ${T.BORDER}`, borderRadius: 8, color: T.DIM, cursor: "pointer", fontSize: 11, fontFamily: "monospace" }}>✕</button>
        </div>
      </div>}
      <StackImportSheet
        show={showImportSheet} files={importFiles} setFiles={setImportFiles}
        plan={importPlan} parseError={importParseError}
        onParse={parseImportFiles} onConfirm={commitImport}
        onClose={() => { setShowImportSheet(false); setImportFiles({ stackItems: null, injectableVials: null, pillBottles: null }); setImportPlan(null); setImportParseError(''); }}
        dm={dm}
      />
      {(undoShot || undoBatch) && (
        <div style={{ position: "fixed", bottom: 88, left: "50%", transform: "translateX(-50%)", background: dm ? "#0d1820" : "#fff", border: `1px solid ${T.BORDER}`, borderRadius: 40, padding: "10px 14px 10px 18px", zIndex: 998, boxShadow: "0 4px 24px rgba(0,0,0,0.3)", fontFamily: "monospace", display: "flex", alignItems: "center", gap: 14, animation: "su 0.2s ease", whiteSpace: "nowrap" }}>
          <span style={{ fontSize: 11, color: T.DIM }}>{undoShot ? "Shot deleted" : "Pills logged"}</span>
          <button onClick={undoShot ? doUndoShot : doUndoBatch} style={{ padding: "6px 16px", background: "rgba(0,216,240,0.1)", color: T.CYAN, fontSize: 11, fontWeight: "700", border: "1px solid rgba(0,216,240,0.3)", borderRadius: 20, cursor: "pointer" }}>Undo</button>
        </div>
      )}

      <style>{`
        @keyframes su{from{opacity:0;transform:translateY(14px);}to{opacity:1;transform:translateY(0);}}
        @keyframes sf{from{opacity:0;transform:translateY(40px);}to{opacity:1;transform:translateY(0);}}
        @keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
        @keyframes pal{0%,100%{opacity:1;}50%{opacity:0.6;}}
        @keyframes mpulse{0%,100%{opacity:1;transform:scale(1);}50%{opacity:0.4;transform:scale(1.15);}}
        .row:hover{background:${dm ? "rgba(0,216,240,0.015)" : "rgba(0,100,140,0.04)"}}
        button:hover{opacity:0.88;}
      `}</style>

      <div style={{ position: "relative", zIndex: 1, maxWidth: 480, margin: "0 auto", paddingBottom: 80 }}>
        {/* Header */}
        <div style={{ padding: "20px 22px 14px", borderBottom: `1px solid ${T.BORDER}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 9, fontFamily: "monospace", letterSpacing: "0.25em", color: dm ? "rgba(0,216,240,0.35)" : "rgba(0,100,140,0.5)", textTransform: "uppercase", marginBottom: 6 }}>Own Your Protocol.</div>
              <img src={dm ? LOGO_WORDMARK : LOGO_LITE} alt="MyRONKO" style={{ height: 26, width: "auto", display: "block" }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button onClick={() => { const nd = !dm; setDark(nd); upd({ darkMode: nd }); }} style={{ background: "none", border: `1px solid ${T.BORDER}`, cursor: "pointer", color: T.DIM, borderRadius: 20, padding: "4px 10px", fontSize: 9, fontFamily: "monospace", display: "flex", alignItems: "center", gap: 4 }}>{dm ? <Sun size={10} /> : <Moon size={10} />}{dm ? "Light" : "Dark"}</button>
                <button onClick={() => supabase.auth.signOut()} style={{ background: "none", border: `1px solid ${T.BORDER}`, cursor: "pointer", color: T.DIM, borderRadius: 20, padding: "4px 10px", fontSize: 9, fontFamily: "monospace", letterSpacing: "0.06em" }}>Sign Out</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div onClick={forceSave} title="Tap to sync" style={{ position: "relative", width: 42, height: 42, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${saveStatus === "error" ? "#e84040" : saveStatus === "saving" ? "#e87030" : "rgba(0,216,240,0.3)"}`, animation: saveStatus === "saving" ? "mpulse 1s ease infinite" : "none" }} />
                  <div style={{ width: 9, height: 9, borderRadius: "50%", background: saveStatus === "error" ? "#e84040" : saveStatus === "saving" ? "#e87030" : T.CYAN, boxShadow: `0 0 8px ${saveStatus === "error" ? "#e84040" : saveStatus === "saving" ? "#e87030" : T.CYAN}` }} />
                </div>
                <div style={{ fontSize: 7, fontFamily: "monospace", color: saveStatus === "error" ? "#e84040" : saveStatus === "saving" ? "#e87030" : dm ? "rgba(0,216,240,0.35)" : "rgba(0,100,140,0.45)" }}>{saveStatus === "error" ? "FAILED" : saveStatus === "saving" ? "SAVING" : "SYNCED"}</div>
              </div>
            </div>
          </div>
        </div>

        <AlertBanner alerts={alerts} onDismiss={dismissAlert} dm={dm} />

        {/* PWA Install Prompt */}
        {showInstallPrompt && installPrompt && (
          <div style={{ padding: "12px 22px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: dm ? "rgba(0,216,240,0.08)" : "rgba(0,112,168,0.07)", border: `1px solid ${dm ? "rgba(0,216,240,0.25)" : "rgba(0,112,168,0.3)"}`, borderRadius: 12, animation: "su 0.3s ease" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: "600", color: T.CYAN, marginBottom: 2 }}>Install MyRONKO</div>
                <div style={{ fontSize: 9, color: T.DIM }}>Add to home screen for quick access</div>
              </div>
              <div style={{ display: "flex", gap: 6, marginLeft: 12 }}>
                <button onClick={async () => { installPrompt.prompt(); const { outcome } = await installPrompt.userChoice; if (outcome === "accepted") setShowInstallPrompt(false); else setShowInstallPrompt(false); }} style={{ padding: "6px 13px", background: T.CYAN, color: dm ? "#0a1828" : "#fff", fontSize: 10, fontWeight: "600", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap" }}>Install</button>
                <button onClick={() => setShowInstallPrompt(false)} style={{ padding: "6px 10px", background: "transparent", color: T.DIM, fontSize: 10, border: `1px solid ${T.BORDER}`, borderRadius: 8, cursor: "pointer" }}>✕</button>
              </div>
            </div>
          </div>
        )}

        {/* Nav */}
        <div style={{ display: "flex", gap: 7, padding: "13px 22px 0", flexWrap: "wrap", alignItems: "center" }}>
          {streakEnabled && cmpdKeys.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 20, background: navStreak > 0 ? "rgba(242,107,59,0.12)" : "rgba(242,107,59,0.05)", border: `1px solid ${navStreak > 0 ? "rgba(242,107,59,0.35)" : "rgba(242,107,59,0.15)"}`, cursor: "pointer" }} data-testid="nav-streak-pill" onClick={() => setView("streak")}>
              <div style={{ position: "relative" }}>
                <Shield size={13} style={{ color: "#f26b3b", display: "block" }} />
                {navStreakSaves > 0 && <span style={{ position: "absolute", top: -4, right: -5, fontSize: 7, fontFamily: "monospace", fontWeight: "700", color: "#0a1828", background: "#f26b3b", borderRadius: "50%", width: 10, height: 10, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>{navStreakSaves}</span>}
              </div>
              <span style={{ fontSize: 11, fontFamily: "monospace", color: "#f26b3b", fontWeight: "700", textTransform: "uppercase" }}>streak</span>
            </div>
          )}
          {PB("Dashboard", view === "dash", () => setView("dash"))}
          {cmpdKeys.length > 0 && PB("Vault", view === "vault", () => setView("vault"))}
          {import.meta.env.DEV && cmpdKeys.length > 0 && PB("Trends", view === "trends", () => setView("trends"))}
          {cmpdKeys.length > 0 && <button onClick={() => setView("history")} style={{ border: `1px solid ${view === "history" ? (dm ? "rgba(0,216,240,0.45)" : "rgba(0,112,168,0.45)") : T.BORDER}`, background: view === "history" ? (dm ? "rgba(0,216,240,0.1)" : "rgba(0,112,168,0.1)") : "transparent", color: view === "history" ? T.CYAN : T.DIM, cursor: "pointer", borderRadius: 20, padding: "5px 10px", fontSize: 11, fontFamily: "monospace", transition: "all 0.18s", display: "flex", alignItems: "center", justifyContent: "center" }}><Calendar size={12} /></button>}
          <button data-testid="settings-btn" onClick={() => setView("settings")} style={{ border: `1px solid ${view === "settings" ? (dm ? "rgba(0,216,240,0.45)" : "rgba(0,112,168,0.45)") : T.BORDER}`, background: view === "settings" ? (dm ? "rgba(0,216,240,0.1)" : "rgba(0,112,168,0.1)") : "transparent", color: view === "settings" ? T.CYAN : T.DIM, cursor: "pointer", borderRadius: 20, padding: "5px 10px", fontSize: 11, fontFamily: "monospace", transition: "all 0.18s", display: "flex", alignItems: "center", justifyContent: "center" }}><Settings size={12} /></button>
        </div>

        {/* Views */}
        <Suspense fallback={null}>
          {view === "dash" && <ErrorBoundary><DashView /></ErrorBoundary>}
          {view === "streak" && <ErrorBoundary><StreakView /></ErrorBoundary>}
          {view === "history" && <ErrorBoundary><HistoryView /></ErrorBoundary>}
          {view === "vault" && <ErrorBoundary><VaultView /></ErrorBoundary>}
          {view === "settings" && <ErrorBoundary><SettingsView /></ErrorBoundary>}
          {view === "trends" && <ErrorBoundary><TrendsView /></ErrorBoundary>}
        </Suspense>

        {/* No Active Vial Prompt */}
        {noVialCmpd && (() => { const c = getCmpd(noVialCmpd); return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={e => { if (e.target === e.currentTarget) setNoVialCmpd(null); }}>
            <div style={{ width: "100%", maxWidth: 480, background: dm ? "#0a1018" : "#f0f8ff", borderRadius: "20px 20px 0 0", padding: 28, animation: "sf 0.25s ease", borderTop: `2px solid ${c.color}66` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: dm ? `${c.dim}99` : `${c.color}33`, border: `1px solid ${c.color}77`, display: "flex", alignItems: "center", justifyContent: "center" }}><CompoundIcon compound={c} size={16} /></div>
                <div>
                  <div style={{ fontSize: 15, color: dm ? c.text : "#0a1828", fontWeight: "600" }}>{c.label}</div>
                  <div style={{ fontSize: 11, color: T.DIM, marginTop: 2 }}>No active vial to log against</div>
                </div>
              </div>
              <div style={{ fontSize: 13, color: dm ? "#c0d4e0" : "#1a3a50", marginBottom: 20, lineHeight: 1.5 }}>Add a vial to your inventory first so MyRONKO can track your remaining supply and calculate your dose.</div>
              <div style={{ display: "flex", gap: 9 }}>
                <button onClick={() => { setNoVialCmpd(null); setView("vault"); setAVF(true); setNVD({ status: "new", active: true, quantity: 1, compound: noVialCmpd }); }} style={{ flex: 1, padding: "12px", background: c.dim, color: c.text, fontSize: 13, fontWeight: "700", border: `1px solid ${c.color}66`, borderRadius: 12, cursor: "pointer" }}>+ Add Vial</button>
                <button onClick={() => setNoVialCmpd(null)} style={{ padding: "12px 16px", background: "transparent", color: T.DIM, fontSize: 13, border: `1px solid ${T.BORDER}`, borderRadius: 12, cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          </div>
        ); })()}
      </div>

      {/* Global Footer */}
      <div style={{ padding: '20px 22px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${T.BORDER}`, marginTop: 12 }}>
        <a href="mailto:care@myronko.com?subject=MyRONKO Bug Report" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, fontFamily: 'monospace', color: T.DIM, textDecoration: 'none', letterSpacing: '0.08em' }}>
          <span style={{ fontSize: 11 }}>🐛</span> Report a Bug
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 7, fontFamily: 'monospace', color: T.CYAN, letterSpacing: '0.12em', border: `1px solid ${T.CYAN}44`, borderRadius: 4, padding: '1px 5px', opacity: 0.7 }}>BETA</span>
          <a href="https://myronko.com" target="_blank" rel="noopener noreferrer" style={{ fontSize: 9, fontFamily: 'monospace', color: T.DIM, textDecoration: 'none', letterSpacing: '0.08em' }}>© MyRONKO</a>
        </div>
      </div>

      {(checkIn || postLogCheckIn) && <CheckInModal
        onSave={saveCheckIn}
        onClose={() => { setCheckIn(false); setPostLogCheckIn(null); }}
        compounds={Object.fromEntries(Object.entries(stored.compounds || {}).filter(([k]) => !(stored.dashHiddenCmpds || []).includes(k)))}
        getCmpd={getCmpd}
        dm={dm}
        defaultCompound={postLogCheckIn || checkInCmpd}
        userWeight={userWeight}
        weightUnit={weightUnit}
        bloodGlucoseUnit={bloodGlucoseUnit}
      />}
      {milestone && <MilestoneCard milestone={milestone} streak={currentStreak} onClose={() => setMilestone(null)} onToast={showToast} dm={dm} />}
      {/* Test-only: MilestoneCard only opens on an unpredictable threshold crossing (see
          MILESTONES_SHOTS/MILESTONES_STREAK), so e2e tests can't reliably trigger it through
          normal UI flow. import.meta.env.MODE is 'test' only under Playwright's webServer
          (vite --mode test); it's never 'test' in real dev or production builds, so this is
          dead code (and dead-code-eliminated) everywhere else. */}
      {import.meta.env.MODE === 'test' && (
        <button data-testid="test-trigger-milestone" onClick={() => setMilestone({ type: 'shots', value: 100 })}
          style={{ position: 'fixed', bottom: 2, right: 2, width: 6, height: 6, padding: 0, border: 'none', opacity: 0.01, zIndex: 999 }} />
      )}
      {showWelcome && <WelcomeFlow darkMode={dm} onComplete={() => { setShowWelcome(false); try { localStorage.setItem('mr_welcome_seen', '1'); } catch {} }} />}
      {showHowTo && <HowToGuide darkMode={dm} onClose={() => setShowHowTo(false)} />}
    </div>
    </TrackerContext.Provider>
  );
}
